import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Rocket,
  Settings,
  Shield,
  CreditCard,
  Mail,
  BarChart3,
  FileText,
  Globe,
  Users,
  CheckCircle2,
  Circle,
  ExternalLink,
  AlertTriangle,
  Megaphone,
  Search,
  Lock,
  Server,
  Palette
} from 'lucide-react';

/**
 * Launch Checklist - Everything needed to go live
 */
const LaunchChecklist = () => {
  const [checklist, setChecklist] = useState(() => {
    const saved = localStorage.getItem('launch_checklist');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('launch_checklist', JSON.stringify(checklist));
  }, [checklist]);

  const toggleItem = (id) => {
    setChecklist(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const categories = [
    {
      id: 'infrastructure',
      title: 'Infrastructure & Hosting',
      icon: Server,
      items: [
        {
          id: 'domain',
          title: 'Domain Name',
          description: 'Register your production domain (e.g., pawsitively.com)',
          link: 'https://domains.google.com',
          priority: 'critical'
        },
        {
          id: 'ssl',
          title: 'SSL Certificate',
          description: 'Ensure HTTPS is enabled (usually automatic with modern hosts)',
          priority: 'critical'
        },
        {
          id: 'hosting',
          title: 'Production Hosting',
          description: 'Deploy to production environment (Vercel, Railway, AWS, etc.)',
          priority: 'critical'
        },
        {
          id: 'cdn',
          title: 'CDN Setup',
          description: 'Configure Cloudflare or similar for global content delivery',
          link: 'https://cloudflare.com',
          priority: 'high'
        },
        {
          id: 'backup',
          title: 'Database Backups',
          description: 'Enable automated MongoDB backups',
          priority: 'critical'
        },
        {
          id: 'monitoring',
          title: 'Uptime Monitoring',
          description: 'Set up uptime monitoring (UptimeRobot, Pingdom)',
          link: 'https://uptimerobot.com',
          priority: 'high'
        }
      ]
    },
    {
      id: 'monetization',
      title: 'Monetization & Payments',
      icon: CreditCard,
      items: [
        {
          id: 'gam',
          title: 'Google Ad Manager',
          description: 'Create GAM account and configure ad units',
          link: 'https://admanager.google.com',
          internalLink: '/admin/ads',
          priority: 'high'
        },
        {
          id: 'stripe',
          title: 'Stripe Account',
          description: 'Set up Stripe for membership payments',
          link: 'https://dashboard.stripe.com/register',
          priority: 'high'
        },
        {
          id: 'stripe_webhooks',
          title: 'Stripe Webhooks',
          description: 'Configure webhook endpoints for payment events',
          priority: 'high'
        },
        {
          id: 'tax',
          title: 'Tax Configuration',
          description: 'Set up Stripe Tax or configure tax rates',
          priority: 'medium'
        }
      ]
    },
    {
      id: 'communications',
      title: 'Email & Communications',
      icon: Mail,
      items: [
        {
          id: 'email_service',
          title: 'Transactional Email Service',
          description: 'Set up SendGrid, Resend, or similar for system emails',
          link: 'https://sendgrid.com',
          priority: 'critical'
        },
        {
          id: 'email_templates',
          title: 'Email Templates',
          description: 'Design welcome, password reset, and notification emails',
          priority: 'high'
        },
        {
          id: 'email_domain',
          title: 'Email Domain Authentication',
          description: 'Configure SPF, DKIM, and DMARC records',
          priority: 'high'
        },
        {
          id: 'newsletter',
          title: 'Newsletter System',
          description: 'Set up Mailchimp or ConvertKit for marketing emails',
          link: 'https://mailchimp.com',
          priority: 'medium'
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Tracking',
      icon: BarChart3,
      items: [
        {
          id: 'ga4',
          title: 'Google Analytics 4',
          description: 'Install GA4 tracking code',
          link: 'https://analytics.google.com',
          priority: 'high'
        },
        {
          id: 'gtm',
          title: 'Google Tag Manager',
          description: 'Set up GTM for easier tag management',
          link: 'https://tagmanager.google.com',
          priority: 'medium'
        },
        {
          id: 'search_console',
          title: 'Google Search Console',
          description: 'Verify site ownership and monitor search performance',
          link: 'https://search.google.com/search-console',
          priority: 'high'
        },
        {
          id: 'hotjar',
          title: 'User Behavior Analytics',
          description: 'Set up Hotjar or similar for heatmaps and recordings',
          link: 'https://hotjar.com',
          priority: 'low'
        }
      ]
    },
    {
      id: 'legal',
      title: 'Legal & Compliance',
      icon: FileText,
      items: [
        {
          id: 'privacy_policy',
          title: 'Privacy Policy',
          description: 'Create and publish privacy policy',
          priority: 'critical'
        },
        {
          id: 'terms',
          title: 'Terms of Service',
          description: 'Create and publish terms of service',
          priority: 'critical'
        },
        {
          id: 'cookie_consent',
          title: 'Cookie Consent Banner',
          description: 'Implement GDPR-compliant cookie consent',
          priority: 'high'
        },
        {
          id: 'dmca',
          title: 'DMCA Policy',
          description: 'Create copyright/DMCA takedown policy',
          priority: 'medium'
        },
        {
          id: 'community_guidelines',
          title: 'Community Guidelines',
          description: 'Publish clear community rules and content policy',
          priority: 'high'
        }
      ]
    },
    {
      id: 'security',
      title: 'Security',
      icon: Shield,
      items: [
        {
          id: 'rate_limiting',
          title: 'Rate Limiting',
          description: 'Ensure API rate limiting is configured',
          priority: 'critical',
          status: 'done'
        },
        {
          id: 'env_vars',
          title: 'Environment Variables',
          description: 'Secure all API keys and secrets in production',
          priority: 'critical'
        },
        {
          id: 'cors',
          title: 'CORS Configuration',
          description: 'Restrict CORS to production domains only',
          priority: 'critical'
        },
        {
          id: 'security_headers',
          title: 'Security Headers',
          description: 'Configure CSP, HSTS, X-Frame-Options, etc.',
          priority: 'high'
        },
        {
          id: 'two_factor',
          title: 'Admin 2FA',
          description: 'Enable two-factor authentication for admin accounts',
          priority: 'high'
        }
      ]
    },
    {
      id: 'seo',
      title: 'SEO & Discoverability',
      icon: Search,
      items: [
        {
          id: 'meta_tags',
          title: 'Meta Tags',
          description: 'Add title, description, and OG tags to all pages',
          priority: 'high'
        },
        {
          id: 'sitemap',
          title: 'XML Sitemap',
          description: 'Generate and submit sitemap to search engines',
          priority: 'high'
        },
        {
          id: 'robots_txt',
          title: 'Robots.txt',
          description: 'Configure robots.txt for proper crawling',
          priority: 'medium'
        },
        {
          id: 'structured_data',
          title: 'Structured Data',
          description: 'Add JSON-LD schema markup for rich snippets',
          priority: 'medium'
        },
        {
          id: 'social_preview',
          title: 'Social Media Previews',
          description: 'Test and optimize link previews on Facebook, Twitter, LinkedIn',
          priority: 'medium'
        }
      ]
    },
    {
      id: 'social',
      title: 'Social Media & Marketing',
      icon: Megaphone,
      items: [
        {
          id: 'facebook_page',
          title: 'Facebook Page',
          description: 'Create official Facebook business page',
          link: 'https://facebook.com/pages/create',
          priority: 'high'
        },
        {
          id: 'instagram',
          title: 'Instagram Account',
          description: 'Create Instagram business account',
          link: 'https://instagram.com',
          priority: 'high'
        },
        {
          id: 'twitter',
          title: 'Twitter/X Account',
          description: 'Create official Twitter presence',
          link: 'https://twitter.com',
          priority: 'medium'
        },
        {
          id: 'tiktok',
          title: 'TikTok Account',
          description: 'Consider TikTok for pet content viral potential',
          link: 'https://tiktok.com',
          priority: 'low'
        },
        {
          id: 'youtube',
          title: 'YouTube Channel',
          description: 'Create channel for tutorials and community content',
          link: 'https://youtube.com',
          priority: 'low'
        }
      ]
    },
    {
      id: 'testing',
      title: 'Testing & Quality',
      icon: CheckCircle2,
      items: [
        {
          id: 'mobile_test',
          title: 'Mobile Responsiveness',
          description: 'Test all pages on mobile devices',
          priority: 'critical'
        },
        {
          id: 'browser_test',
          title: 'Cross-Browser Testing',
          description: 'Test on Chrome, Firefox, Safari, Edge',
          priority: 'high'
        },
        {
          id: 'load_test',
          title: 'Load Testing',
          description: 'Run load tests to verify performance under traffic',
          priority: 'high'
        },
        {
          id: 'accessibility',
          title: 'Accessibility Audit',
          description: 'Run WAVE or Lighthouse accessibility checks',
          priority: 'high'
        },
        {
          id: 'user_testing',
          title: 'Beta User Testing',
          description: 'Get feedback from real users before launch',
          priority: 'high'
        }
      ]
    }
  ];

  // Calculate progress
  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const completedItems = Object.values(checklist).filter(Boolean).length;
  const progress = Math.round((completedItems / totalItems) * 100);

  const getPriorityBadge = (priority) => {
    const variants = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-amber-100 text-amber-800',
      medium: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={variants[priority]}>{priority}</Badge>;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="launch-checklist">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
          <Rocket className="w-8 h-8 text-primary" />
          Launch Checklist
        </h1>
        <p className="text-muted-foreground mt-2">
          Everything you need to prepare before going live with your platform
        </p>
      </div>

      {/* Progress Overview */}
      <Card className="mb-8">
        <CardContent className="py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium">Launch Readiness</h3>
              <p className="text-sm text-muted-foreground">
                {completedItems} of {totalItems} items completed
              </p>
            </div>
            <div className="text-3xl font-bold text-primary">{progress}%</div>
          </div>
          <Progress value={progress} className="h-3" />
          
          {progress === 100 && (
            <Alert className="mt-4 bg-green-50 border-green-200">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Congratulations! You've completed all checklist items. You're ready to launch! 🚀
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {['critical', 'high', 'medium', 'low'].map((priority) => {
          const items = categories.flatMap(c => c.items).filter(i => i.priority === priority);
          const done = items.filter(i => checklist[i.id]).length;
          return (
            <Card key={priority}>
              <CardContent className="py-4 text-center">
                <div className="text-2xl font-bold">{done}/{items.length}</div>
                <div className="text-sm text-muted-foreground capitalize">{priority} Priority</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Checklist Categories */}
      <div className="space-y-6">
        {categories.map((category) => {
          const categoryDone = category.items.filter(i => checklist[i.id]).length;
          const CategoryIcon = category.icon;
          
          return (
            <Card key={category.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CategoryIcon className="w-5 h-5 text-primary" />
                    {category.title}
                  </span>
                  <Badge variant="outline">
                    {categoryDone}/{category.items.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {category.items.map((item) => (
                    <div 
                      key={item.id}
                      className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                        checklist[item.id] ? 'bg-green-50' : 'hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        id={item.id}
                        checked={checklist[item.id] || false}
                        onCheckedChange={() => toggleItem(item.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={item.id}
                          className={`font-medium cursor-pointer ${
                            checklist[item.id] ? 'line-through text-muted-foreground' : ''
                          }`}
                        >
                          {item.title}
                        </label>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {item.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {getPriorityBadge(item.priority)}
                          {item.status === 'done' && (
                            <Badge variant="outline" className="text-green-600">
                              Already Implemented
                            </Badge>
                          )}
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              Setup Guide <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {item.internalLink && (
                            <a
                              href={item.internalLink}
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              Configure <Settings className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Reset Button */}
      <div className="mt-8 text-center">
        <Button 
          variant="outline" 
          onClick={() => {
            if (confirm('Reset all checklist progress?')) {
              setChecklist({});
            }
          }}
        >
          Reset Checklist
        </Button>
      </div>
    </div>
  );
};

export default LaunchChecklist;
